import { useEffect, useState, useCallback, useRef } from 'react';

interface UseTelegramReturn {
  isReady: boolean;
  isTelegram: boolean;
  initData: string | undefined;

  // Main Button
  showMainButton: (text: string, onClick: () => void) => void;
  hideMainButton: () => void;
  setMainButtonLoading: (loading: boolean) => void;
  setMainButtonDisabled: (disabled: boolean) => void;

  // Back Button
  showBackButton: (onClick: () => void) => void;
  hideBackButton: () => void;

  // Haptic Feedback
  impactOccurred: (style?: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
  notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  selectionChanged: () => void;

  // Alerts
  showAlert: (message: string) => void;
  showConfirm: (message: string) => Promise<boolean>;

  // App Controls
  close: () => void;
  expand: () => void;

  // Theme
  colorScheme: 'light' | 'dark';
}

// Check if running in Telegram
const checkIsTelegram = (): boolean => {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp?.initData;
};

export const useTelegram = (): UseTelegramReturn => {
  const [isReady, setIsReady] = useState(false);
  const [isTelegram, setIsTelegram] = useState(false);
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>('light');
  const mainButtonCallbackRef = useRef<(() => void) | null>(null);
  const backButtonCallbackRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const isTg = checkIsTelegram();
    setIsTelegram(isTg);

    if (isTg && window.Telegram?.WebApp) {
      const webApp = window.Telegram.WebApp;

      // Signal ready
      webApp.ready?.();

      // Expand viewport
      webApp.expand?.();

      // Get color scheme
      setColorScheme(webApp.colorScheme || 'light');

      // Listen for theme changes
      webApp.onEvent?.('themeChanged', () => {
        setColorScheme(webApp.colorScheme || 'light');
      });
    } else {
      // Development mode - check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setColorScheme(prefersDark ? 'dark' : 'light');
      console.log('Running outside Telegram - development mode');
    }

    setIsReady(true);
  }, []);

  // Main Button
  const showMainButton = useCallback((text: string, onClick: () => void) => {
    if (!checkIsTelegram() || !window.Telegram?.WebApp?.MainButton) {
      console.log('[Dev] MainButton.show:', text);
      mainButtonCallbackRef.current = onClick;
      return;
    }

    const mainButton = window.Telegram.WebApp.MainButton;
    mainButtonCallbackRef.current = onClick;

    mainButton.setText(text);
    mainButton.show();
    mainButton.enable();
    mainButton.onClick(onClick);
  }, []);

  const hideMainButton = useCallback(() => {
    if (!checkIsTelegram() || !window.Telegram?.WebApp?.MainButton) {
      console.log('[Dev] MainButton.hide');
      return;
    }

    const mainButton = window.Telegram.WebApp.MainButton;
    if (mainButtonCallbackRef.current) {
      mainButton.offClick(mainButtonCallbackRef.current);
      mainButtonCallbackRef.current = null;
    }
    mainButton.hide();
  }, []);

  const setMainButtonLoading = useCallback((loading: boolean) => {
    if (!checkIsTelegram() || !window.Telegram?.WebApp?.MainButton) {
      console.log('[Dev] MainButton.loading:', loading);
      return;
    }

    const mainButton = window.Telegram.WebApp.MainButton;
    if (loading) {
      mainButton.showProgress();
    } else {
      mainButton.hideProgress();
    }
  }, []);

  const setMainButtonDisabled = useCallback((disabled: boolean) => {
    if (!checkIsTelegram() || !window.Telegram?.WebApp?.MainButton) {
      console.log('[Dev] MainButton.disabled:', disabled);
      return;
    }

    const mainButton = window.Telegram.WebApp.MainButton;
    if (disabled) {
      mainButton.disable();
    } else {
      mainButton.enable();
    }
  }, []);

  // Back Button
  const showBackButton = useCallback((onClick: () => void) => {
    if (!checkIsTelegram() || !window.Telegram?.WebApp?.BackButton) {
      console.log('[Dev] BackButton.show');
      backButtonCallbackRef.current = onClick;
      return;
    }

    const backButton = window.Telegram.WebApp.BackButton;
    backButtonCallbackRef.current = onClick;
    backButton.show();
    backButton.onClick(onClick);
  }, []);

  const hideBackButton = useCallback(() => {
    if (!checkIsTelegram() || !window.Telegram?.WebApp?.BackButton) {
      console.log('[Dev] BackButton.hide');
      return;
    }

    const backButton = window.Telegram.WebApp.BackButton;
    if (backButtonCallbackRef.current) {
      backButton.offClick(backButtonCallbackRef.current);
      backButtonCallbackRef.current = null;
    }
    backButton.hide();
  }, []);

  // Haptic Feedback
  const impactOccurred = useCallback((style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
    if (!checkIsTelegram()) return;
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
    } catch (e) {
      // Haptic not available
    }
  }, []);

  const notificationOccurred = useCallback((type: 'error' | 'success' | 'warning') => {
    if (!checkIsTelegram()) return;
    try {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
    } catch (e) {
      // Haptic not available
    }
  }, []);

  const selectionChanged = useCallback(() => {
    if (!checkIsTelegram()) return;
    try {
      window.Telegram?.WebApp?.HapticFeedback?.selectionChanged();
    } catch (e) {
      // Haptic not available
    }
  }, []);

  // Alerts
  const showAlert = useCallback((message: string) => {
    if (checkIsTelegram() && window.Telegram?.WebApp?.showAlert) {
      window.Telegram.WebApp.showAlert(message);
    } else {
      alert(message);
    }
  }, []);

  const showConfirm = useCallback(async (message: string): Promise<boolean> => {
    if (checkIsTelegram() && window.Telegram?.WebApp?.showConfirm) {
      return new Promise((resolve) => {
        window.Telegram?.WebApp?.showConfirm?.(message, (confirmed) => {
          resolve(confirmed);
        });
      });
    }
    return confirm(message);
  }, []);

  // App Controls
  const close = useCallback(() => {
    if (checkIsTelegram() && window.Telegram?.WebApp?.close) {
      window.Telegram.WebApp.close();
    }
  }, []);

  const expand = useCallback(() => {
    if (checkIsTelegram() && window.Telegram?.WebApp?.expand) {
      window.Telegram.WebApp.expand();
    }
  }, []);

  return {
    isReady,
    isTelegram,
    initData: checkIsTelegram() ? window.Telegram?.WebApp?.initData : undefined,

    // Main Button
    showMainButton,
    hideMainButton,
    setMainButtonLoading,
    setMainButtonDisabled,

    // Back Button
    showBackButton,
    hideBackButton,

    // Haptic Feedback
    impactOccurred,
    notificationOccurred,
    selectionChanged,

    // Alerts
    showAlert,
    showConfirm,

    // App Controls
    close,
    expand,

    // Theme
    colorScheme,
  };
};

// Type declaration is in src/types/telegram.d.ts
